import { Typography } from '@material-ui/core';
import { Trip, TripId, TripStatus, TripVersion, Money, DepositSettings, TripWithVersion } from '@tripr/common';
import React, { useState } from 'react';
import { AccountApi, HostApi, TripWithVersionApi } from '../../api/RpcClient';
import { useToast } from '../../utils/Utils';
import { LoadingGuard } from '../common/LoadingGuard';
import { TripForm, TripSchema, TripValues } from './components/TripForm';
import { useAuthContext } from '../../utils/AuthContext';
import { ContentfulApiClient } from '../../api/ContentfulClient';

export const TripEditPage: React.FC<{ tripId: TripId }> = props => {
  const [callWithToast] = useToast('trip');
  // using this stat to pass updated slug to TripForm so it can be used for 'Show preview' button in Edit page
  const [slug, setSlug] = useState('');
  const [trip, setTrip] = useState<TripWithVersion | null>(null);
  const authContext = useAuthContext();

  const accountId = authContext.isLoggedIn ? authContext.account.accountId : null;

  return (
    <LoadingGuard
      loadData={async () => {
        const hosts = await HostApi.getHostOptions();
        const trip = await TripWithVersionApi.getWithVersion(TripId(props.tripId));
        const tripOptions = await TripWithVersionApi.getRecommendedTripOptions();
        // get articles categories from contentful
        const articleCategories = await TripWithVersionApi.getContentfulCategories();
        const landings = await TripWithVersionApi.getContentfulLandings();
        setSlug(trip.currentVersion.slug);
        setTrip(trip);
        return { hosts, trip, tripOptions, articleCategories, landings };
      }}
    >
      {({ hosts, trip: initialTrip, tripOptions, articleCategories, landings }) => {
        // Use local state if available, otherwise fall back to LoadingGuard data
        const currentTrip = trip || initialTrip;

        // Ensure currentVersion exists (it should always exist for TripWithVersion)
        if (!currentTrip.currentVersion) {
          return <div>Error: Trip version not found</div>;
        }

        return (
          <div>
            <div style={{ display: 'flex' }}>
              <Typography gutterBottom variant="h3">
                {currentTrip.currentVersion.title}
              </Typography>
            </div>
            <TripForm
              slug={slug}
              trip={{
                ...currentTrip.currentVersion,
                status: currentTrip.status,
                active: currentTrip.active,
                featured: currentTrip.featured,
                hidden: currentTrip.hidden,
                featuredPosition: currentTrip.featuredPosition,
                deleted: currentTrip.deleted,
                hostProfileId: currentTrip.hostProfileId,
                createdById: currentTrip.createdById,
                tripInclusions: currentTrip.currentVersion.tripInclusions,
                uploaderEmail: currentTrip.uploader?.email || null,
                redirect: currentTrip.currentVersion.redirect === undefined ? null : currentTrip.currentVersion.redirect,
              }}
              hosts={hosts}
              tripOptions={tripOptions}
              articleCategories={articleCategories}
              landings={landings}
              onSubmit={async (values: TripValues) => {
                try {
                  console.log('values', values);
                  const { hostProfileId, createdById, status, active, featured, deleted, featuredPosition, hidden, uploaderEmail, ...tripVersionValues } =
                    TripSchema.cast(values);
                  const { currentVersion, uploader, ...tripFields } = currentTrip;
                  const newTrip: Trip = {
                    ...tripFields,
                    status: status as TripStatus,
                    active,
                    deleted,
                    featured,
                    hidden,
                    featuredPosition,
                    hostProfileId,
                    createdById,
                  };

                  if (authContext.isLoggedIn) {
                    newTrip.uploaderId = authContext.account.accountId;
                  }

                  // Ensure depositSettings uses Money type
                  if (tripVersionValues.depositSettings) {
                    const settings = tripVersionValues.depositSettings as any;
                    tripVersionValues.depositSettings = {
                      ...settings,
                      amount: Money(settings.amount),
                      minPricePerPerson: Money(settings.minPricePerPerson),
                    } as DepositSettings;
                  }

                  const tripVersion: TripVersion = {
                    ...(tripVersionValues as unknown as Omit<TripVersion, 'tripId' | 'tripVersionId'>),
                    tripId: currentTrip.tripId,
                    tripVersionId: currentTrip.currentVersion.tripVersionId,
                  };
                  // making first image of the gallery as cover image
                  if (values && tripVersionValues?.gallery.length > 0) {
                    // Ensure coverImage has the correct structure as per UploadedImage type
                    const galleryImage = tripVersionValues.gallery[0];
                    if (galleryImage && 'hash' in galleryImage && 'lqip' in galleryImage) {
                      tripVersion.coverImage = galleryImage;
                    }
                  }
                  // return console.log(tripVersion);
                  await callWithToast(TripWithVersionApi.updateTripWithVersion(newTrip, tripVersion));

                  // Update local state with the new trip data
                  const updatedTrip: TripWithVersion = {
                    ...newTrip,
                    currentVersion: tripVersion,
                    currentVersionId: tripVersion.tripVersionId,
                  };
                  setSlug(tripVersion.slug);
                  setTrip(updatedTrip);
                } catch (e) {
                  console.log(e);
                }
              }}
            />
          </div>
        );
      }}
    </LoadingGuard>
  );
};
